export const ACCESS_API_VERSION = 'access.bootsman.io/v1alpha1' as const;
export const ACCESS_RESOURCE_NAME_MAX_LENGTH = 253;
export const ACCESS_RESOURCE_NAME_RULE_DESCRIPTION =
  'Use lowercase letters, digits, `-` and `.`, and start and end with a letter or digit.';

const ACCESS_RESOURCE_NAME_PATTERN = /^[a-z0-9](?:[a-z0-9-.]*[a-z0-9])?$/;

export function validateAccessResourceName(name: string): string | undefined {
  if (!name) {
    return 'metadata.name is required';
  }

  if (name.length > ACCESS_RESOURCE_NAME_MAX_LENGTH) {
    return `metadata.name must be no more than ${ACCESS_RESOURCE_NAME_MAX_LENGTH} characters`;
  }

  if (!ACCESS_RESOURCE_NAME_PATTERN.test(name)) {
    return `metadata.name must be a lowercase RFC 1123 subdomain. ${ACCESS_RESOURCE_NAME_RULE_DESCRIPTION}`;
  }

  return undefined;
}

export type GitflicPermission =
  | 'Guest'
  | 'Reporter'
  | 'Developer'
  | 'Administrator'
  | 'Owner';

export type BootsmanClusterBuiltinRole = 'cluster-owner' | 'cluster-member';

export type BootsmanProjectBuiltinRole =
  | 'bootsman-project-admin'
  | 'bootsman-project-member'
  | 'bootsman-project-viewer';

export type BootsmanClusterGrant = {
  level: 'cluster';
  assignment: {
    kind: 'bootsmanClusterRole';
    builtin: BootsmanClusterBuiltinRole;
  };
};

export type BootsmanProjectGrant = {
  level: 'project';
  assignment: {
    kind: 'bootsmanProjectRole';
    builtin: BootsmanProjectBuiltinRole;
  };
};

export type BootsmanGrant = BootsmanClusterGrant | BootsmanProjectGrant;

export type BootsmanPermissions = {
  grants: BootsmanGrant[];
};

export type AccessRolePermissions = {
  gitflic?: {
    role?: GitflicPermission;
  };
  bootsman?: BootsmanPermissions;
};

export type AccessRoleScope =
  | 'component'
  | 'cluster'
  | 'project'
  | 'mixed'
  | 'invalid';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

export function classifyAccessRolePermissions(
  permissions: unknown,
): AccessRoleScope {
  if (!isRecord(permissions)) {
    return 'invalid';
  }

  const hasGitflicScope = permissions.gitflic !== undefined;
  const bootsman = permissions.bootsman;
  const grants =
    isRecord(bootsman) && Array.isArray(bootsman.grants)
      ? bootsman.grants
      : undefined;
  const grantScopes = grants?.map(grant =>
    isRecord(grant) && (grant.level === 'cluster' || grant.level === 'project')
      ? grant.level
      : undefined,
  );
  const hasInvalidGrantScope = grantScopes?.some(scope => !scope);
  const hasClusterScope = grantScopes?.some(scope => scope === 'cluster');
  const hasProjectScope = grantScopes?.some(scope => scope === 'project');
  const hasBootsmanScope = Boolean(hasClusterScope || hasProjectScope);

  if (
    (hasGitflicScope && hasBootsmanScope) ||
    (hasClusterScope && hasProjectScope)
  ) {
    return 'mixed';
  }

  if (hasGitflicScope) {
    return 'component';
  }

  if (hasInvalidGrantScope || !hasBootsmanScope) {
    return 'invalid';
  }

  return hasClusterScope ? 'cluster' : 'project';
}

export function validateAccessRolePermissions(
  permissions: unknown,
): string | undefined {
  const scope = classifyAccessRolePermissions(permissions);

  return scope === 'mixed'
    ? 'AccessRole permissions cannot combine different scopes: component, cluster, or project.'
    : undefined;
}

export type AccessRoleSpec = {
  backstage?: {
    roleEntityRef?: string;
  };
  permissions?: AccessRolePermissions;
};

export type AccessBindingSubject = {
  kind: 'Group' | 'User';
  ref: string;
};

export type AccessBindingTargetRef =
  | {
      kind: 'Component';
      ref: string;
    }
  | {
      kind: 'Resource';
      ref: string;
    };

export type AccessBindingSpec = {
  subjects: AccessBindingSubject[];
  roleRef: {
    name: string;
  };
  targetRef?: AccessBindingTargetRef;
};

export type AccessResourceMetadata = {
  uid?: string;
  name: string;
  description?: string;
  createdAt?: string;
  updatedAt?: string;
};

export type AccessResourcePhase =
  | 'pending'
  | 'reconciling'
  | 'ready'
  | 'failed'
  | 'deleting'
  | 'delete_failed';

export type AccessResourceStatus = {
  phase: AccessResourcePhase;
  message?: string;
  lastReconciledAt?: string;
  lastRunId?: string;
  deletion?: {
    requestedAt: string;
    finalizers: string[];
  };
};

export type AccessRoleResource = {
  apiVersion: typeof ACCESS_API_VERSION;
  kind: 'AccessRole';
  metadata: AccessResourceMetadata;
  spec: AccessRoleSpec;
  status?: AccessResourceStatus;
};

export type AccessBindingResource = {
  apiVersion: typeof ACCESS_API_VERSION;
  kind: 'AccessBinding';
  metadata: AccessResourceMetadata;
  spec: AccessBindingSpec;
  status?: AccessResourceStatus;
};

export type AccessRoleInput = {
  metadata?: {
    name?: string;
    description?: string;
  };
  spec?: AccessRoleSpec;
  status?: AccessResourceStatus;
};

export type AccessBindingInput = {
  metadata?: {
    name?: string;
    description?: string;
  };
  spec?: AccessBindingSpec;
  status?: AccessResourceStatus;
};

export type AccessRolesListResponse = {
  items: AccessRoleResource[];
};

export type AccessBindingsListResponse = {
  items: AccessBindingResource[];
};

export type AccessAuditAction = 'create' | 'update' | 'delete';

export type AccessAuditResourceKind =
  | AccessRoleResource['kind']
  | AccessBindingResource['kind']
  | 'RBAC';

export type AccessRbacAuditJsonValue =
  | string
  | number
  | boolean
  | null
  | AccessRbacAuditJsonValue[]
  | { [key: string]: AccessRbacAuditJsonValue };

export type AccessRbacAuditEvent = {
  /** Existing version 1 rows remain readable; new rows use version 2. */
  schemaVersion: 1 | 2;
  eventUid: string;
  plugin: 'permission';
  eventId: 'role-write' | 'policy-write' | 'condition-write';
  severityLevel: 'low' | 'medium' | 'high' | 'critical';
  status: 'succeeded';
  actor: {
    actorId?: string;
    ip?: string;
    hostname?: string;
    userAgent?: string;
  };
  request?: {
    method: string;
    path: string;
  };
  meta: {
    actionType: AccessAuditAction;
    source: 'rest';
    response?: {
      status: number;
    };
    roleEntityRef?: string;
    roleEntityRefs?: string[];
    /** User and group entity refs assigned to the role at mutation time. */
    members?: string[];
    /** Sanitized copy of the body submitted by the REST form. */
    requestBody?: AccessRbacAuditJsonValue;
    policies?: Array<{
      entityReference?: string;
      permission?: string;
      policy?: string;
      effect?: string;
    }>;
    conditions?: Array<{
      roleEntityRef?: string;
      pluginId?: string;
      resourceType?: string;
      rule?: string;
    }>;
  };
  resourceKind: 'RBAC';
  resourceRef: string;
  resourceName: string;
  action: AccessAuditAction;
};

export type AccessAuditEntry = {
  id: string;
  eventUid?: string;
  resourceKind: AccessAuditResourceKind;
  resourceRef: string;
  resourceName: string;
  action: AccessAuditAction;
  actorRef?: string;
  actorDisplay: string;
  createdAt: string;
  payload?: unknown;
};

export type AccessAuditLogListResponse = {
  items: AccessAuditEntry[];
  total: number;
  limit: number;
  offset: number;
};

export type AccessSummary = {
  roles: number;
  bindings: number;
};

export type AccessReconcileRequest = {
  roleIds?: string[];
  bindingIds?: string[];
  dryRun?: boolean;
};

type AccessReconcileTrigger = (requestedBy?: string) => void;

let accessReconcileTrigger: AccessReconcileTrigger | undefined;

export function registerAccessReconcileTrigger(
  trigger: AccessReconcileTrigger,
): () => void {
  accessReconcileTrigger = trigger;
  return () => {
    if (accessReconcileTrigger === trigger) {
      accessReconcileTrigger = undefined;
    }
  };
}

export function requestAccessReconcile(requestedBy?: string): boolean {
  if (!accessReconcileTrigger) {
    return false;
  }

  accessReconcileTrigger(requestedBy);
  return true;
}

export type AccessReconcileRunStatus =
  | 'accepted'
  | 'running'
  | 'completed'
  | 'failed'
  | 'aborted';

export type AccessReconcileRunTrigger = 'manual' | 'scheduled';

export type AccessReconcileRunSummary = {
  id: string;
  status: AccessReconcileRunStatus;
  trigger: AccessReconcileRunTrigger;
  dryRun: boolean;
  filters: {
    roleIds: string[];
    bindingIds: string[];
  };
  startedAt?: string;
  finishedAt?: string;
  createdAt?: string;
  message?: string;
  stats?: {
    scannedBindings?: number;
    scannedRoles?: number;
    created?: number;
    updated?: number;
    deleted?: number;
    skipped?: number;
    failed?: number;
    users?: number;
    taskBreakdown?: Record<
      string,
      {
        scannedBindings?: number;
        scannedRoles?: number;
        created?: number;
        updated?: number;
        deleted?: number;
        skipped?: number;
        failed?: number;
        users?: number;
      }
    >;
  };
};

export type AccessReconcileResponse = {
  status: 'accepted';
  runId: string;
  dryRun: boolean;
  filters: {
    roleIds: string[];
    bindingIds: string[];
  };
  summary: AccessSummary;
  message: string;
};

export type AccessReconcileCleanTablesAllowedResponse = {
  allowed: boolean;
};

export type AccessReconcileCleanTablesResponse = {
  status: 'ok';
  cleaned: {
    runs: number;
    resourceStates: number;
    materializations: number;
    journal: number;
  };
  message: string;
};

export type AccessReconcileRunsListResponse = {
  items: AccessReconcileRunSummary[];
  total: number;
  limit: number;
  offset: number;
};

export type AccessReconcileResourceState = {
  resourceKind: string;
  resourceRef: string;
  resourceName: string;
  phase: string;
  message?: string;
  lastRunId?: string;
  lastReconciledAt?: string;
  deletionRequestedAt?: string;
  finalizers: string[];
};

export type AccessReconcileMaterialization = {
  id: string;
  ownerKind: string;
  ownerRef: string;
  taskId: string;
  materializationKey: string;
  materializationType: string;
  externalRef?: string;
  payload?: unknown;
  status: string;
  createdAt: string;
  updatedAt: string;
  appliedAt?: string;
  deletedAt?: string;
};

export type AccessReconcileJournalEntry = {
  id: string;
  runId?: string;
  taskId: string;
  ownerKind?: string;
  ownerRef?: string;
  subjectRef?: string;
  targetRef?: string;
  action: string;
  message: string;
  payload?: unknown;
  createdAt: string;
};

export type AccessReconcileRunDetails = {
  run: AccessReconcileRunSummary;
  resourceStates: AccessReconcileResourceState[];
  materializations: AccessReconcileMaterialization[];
  journal: AccessReconcileJournalEntry[];
};

export type AccessReconcileResourceStatesListResponse = {
  items: AccessReconcileResourceState[];
};

export type AccessReconcileMaterializationsListResponse = {
  items: AccessReconcileMaterialization[];
};

export type AccessReconcileJournalListResponse = {
  items: AccessReconcileJournalEntry[];
};

export const ACCESS_RESOURCE_SIGNAL_CHANNEL = 'access.resources' as const;
export const ACCESS_RECONCILE_SIGNAL_CHANNEL = 'access.reconcile' as const;

export type AccessSignalEnvelope<TName extends string, TData> = {
  name: TName;
  revision: string;
  data: TData;
};

export type AccessResourceSignalEvent =
  | 'created'
  | 'updated'
  | 'marked_for_deletion'
  | 'status_changed'
  | 'deleted';

export type AccessResourceSignalData = {
  event: AccessResourceSignalEvent;
  resourceKind: AccessRoleResource['kind'] | AccessBindingResource['kind'];
  resourceRef: string;
  resourceName: string;
  phase?: AccessResourcePhase;
  message?: string;
  runId?: string;
  changedAt: string;
};

export type AccessResourceSignal = AccessSignalEnvelope<
  'access.resource.change',
  AccessResourceSignalData
>;

export type AccessReconcileSignalEvent =
  | 'run_accepted'
  | 'run_started'
  | 'run_finished'
  | 'resources_changed';

export type AccessReconcileSignalData = {
  event: AccessReconcileSignalEvent;
  runId?: string;
  status?: AccessReconcileRunStatus;
  dryRun?: boolean;
  changedAt: string;
  stats?: AccessReconcileRunSummary['stats'];
  affectedKinds?: Array<
    AccessRoleResource['kind'] | AccessBindingResource['kind']
  >;
};

export type AccessReconcileSignal = AccessSignalEnvelope<
  'access.reconcile.change',
  AccessReconcileSignalData
>;

export function createAccessResourceSignal(
  data: Omit<AccessResourceSignalData, 'changedAt'> & { changedAt?: string },
): AccessResourceSignal {
  const changedAt = data.changedAt ?? new Date().toISOString();

  return {
    name: 'access.resource.change',
    revision: `${changedAt}:${data.resourceKind}:${data.resourceRef}:${data.event}`,
    data: {
      ...data,
      changedAt,
    },
  };
}

export function createAccessReconcileSignal(
  data: Omit<AccessReconcileSignalData, 'changedAt'> & { changedAt?: string },
): AccessReconcileSignal {
  const changedAt = data.changedAt ?? new Date().toISOString();

  return {
    name: 'access.reconcile.change',
    revision: `${data.runId ?? 'none'}:${data.event}:${changedAt}`,
    data: {
      ...data,
      changedAt,
    },
  };
}

export type AccessHealthResponse = {
  status: 'ok';
};

export { AccessDatabase } from './AccessDatabase';
export { AccessSignalsPublisher } from './AccessSignalsPublisher';
export * from './builtInAccessRoles';
export * from './gitflic';
export * from './permissions';
